import { createSelector } from 'reselect'
import { getTourV2Items } from '../toursv2'
import { excludeNullOrUndefined } from 'checkout/utils'
import { getDepositServiceFeeConfig } from '../../featureConfig/deposit'
import { getFullOffers } from 'selectors/offerSelectors'
import getFlightBundledItemIds from '../getFlightBundledItemIds'
import { groupBy, nonNullable } from 'lib/array/arrayUtils'
import { isTourV2Offer } from 'lib/offer/offerTypes'
import { getTourV2ItemView } from 'checkout/lib/utils/tours/view'
import { getLabels } from 'checkout/lib/utils/accommodation/label'
import { getLocationString } from 'checkout/lib/utils/accommodation/location'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import productLabels from 'i18n/productLabels'
import offerPageURL from 'lib/offer/offerPageURL'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import getAccommodationBundledItemIds from 'checkout/selectors/view/getAccommodationBundledItemIds'
import { formatRoomType } from 'lib/tours/tourUtils'
import {
  PRODUCT_TYPE_TOUR_V2,
  PRODUCT_TYPE_TOUR_V2_CONNECTION,
} from 'constants/offer'
import { TOUR_BALANCE_PAYMENT_CHECKOUT_MODES } from 'constants/checkout'

function getOrderTourItemForGroup(
  existingOrder: App.Order | undefined,
  offerId: string,
  departureId: string,
): App.Tours.TourV2OrderItem | undefined {
  return existingOrder?.tourItems.find(orderItem =>
    orderItem.tourId === offerId &&
    orderItem.departureId === departureId,
  )
}

function formatLocationLabel(startLocation?: string | null, endLocation?: string | null): string | undefined {
  const location = [startLocation, endLocation].filter(Boolean).join(' - ')

  return location || undefined
}

function getSnapshotTourV2ItemView(
  intl: ReturnType<typeof getCurrentIntl>,
  item: App.Checkout.TourV2Item,
  orderTourItem: App.Tours.TourV2OrderItem | undefined,
): App.Checkout.TourV2AccommodationItemView {
  const productType = orderTourItem?.productType
  const loyaltyProductType = productType === PRODUCT_TYPE_TOUR_V2 ? 'le_tour' :
    productType === PRODUCT_TYPE_TOUR_V2_CONNECTION ? 'tour_bank' :
      'trusted_partner_tour'

  return {
    title: '',
    kind: 'tourV2',
    offerId: item.offerId,
    packageId: item.purchasableOption.fkDepartureId,
    uniqueKey: item.purchasableOption.fkDepartureId,
    item,
    primaryTitle: formatRoomType(item.purchasableOption.roomType) ?? '',
    occupancy: item.occupancy,
    removeLabel: intl.formatMessage({
      id: 'myXq1fL3RZecTsJULM0U',
      defaultMessage: 'Remove room',
      description: 'Remove button label for tour room item in checkout',
    }),
    loyalty: {
      productType: loyaltyProductType,
    },
    totals: {
      price: 0,
      memberPrice: 0,
      value: 0,
      surcharge: 0,
      taxesAndFees: 0,
      memberValue: 0,
      extraGuestSurcharge: 0,
    },
  }
}

function getSnapshotTourV2OfferView(
  items: Array<App.Checkout.TourV2Item>,
  orderTourItem: App.Tours.TourV2OrderItem | undefined,
  snapshot: App.Tours.TourV2OfferSnapshot | undefined,
  serviceFee: App.Checkout.ServiceFeeSettings | undefined,
): App.Checkout.TourV2AccommodationOfferView {
  const intl = getCurrentIntl()
  const firstItem = items[0]
  const { startDate, endDate, duration, image } = firstItem
  const snapshotImage = snapshot?.imageId ? { id: snapshot.imageId } : undefined

  return {
    offerId: firstItem.offerId,
    offerPageUrl: '',
    mainLabel: snapshot?.seasonName ?? orderTourItem?.tour.tourOptionTitle ?? orderTourItem?.tour.title ?? '',
    subLabel: snapshot?.tourName ?? orderTourItem?.tour.title,
    bedGroups: [],
    confidenceLabels: [],
    duration,
    startDate: snapshot?.startDate ?? startDate,
    endDate: snapshot?.endDate ?? endDate,
    image: image?.id ? image : snapshotImage ?? image,
    location: formatLocationLabel(snapshot?.startLocation, snapshot?.endLocation) ??
      formatLocationLabel(orderTourItem?.tour.startLocation, orderTourItem?.tour.endLocation) ??
      '',
    occupancy: items.map(item => item.occupancy).filter(excludeNullOrUndefined),
    offerType: 'tour_v2',
    reservationType: 'instant_booking',
    urgencyLabels: [],
    saleUnit: 'room',
    durationLabel: intl.formatMessage(
      {
        id: 'DzOQT2DMDMKAv2kdwkwI',
        defaultMessage: '{count, plural, one {# day} other {# days}}',
        description: 'Tour duration label with pluralization on the tour offer detail page',
      },
      { count: duration },
    ),
    offerLoaded: true,
    itemViews: items.map(item => getSnapshotTourV2ItemView(intl, item, orderTourItem)),
    propertyTimezone: 'GMT',
    operatorName: snapshot?.operatedBy ? intl.formatMessage(
      {
        id: 'iEiRM4lSHHm4Jdq_oqml',
        defaultMessage: 'Operated by: {operatorName}',
        description: 'Label displaying the tour operator name on the tour offer detail overview panel',
      },
      { operatorName: snapshot.operatedBy },
    ) : undefined,
    designation: intl.formatMessage(productLabels.tour),
    serviceFee,
  }
}

const getTourV2ItemViews = createSelector(
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => getDepositServiceFeeConfig(state),
  (state: App.State) => getFullOffers(state),
  (state: App.State) => getFlightBundledItemIds(state),
  (state: App.State) => getAccommodationBundledItemIds(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.order.tourV2SnapshotDetails,
  (
    cartItems,
    serviceFee,
    offers,
    flightBundledItemIds,
    accommodationBundledItemIds,
    postPurchase,
    existingOrder,
    tourV2SnapshotDetails,
  ): App.WithDataStatus<Array<App.Checkout.TourV2AccommodationOfferView>> => {
    const intl = getCurrentIntl()
    // Each item = 1 person, but we want to process the same departure/offer as a single view
    const itemsByBookingKey = groupBy(cartItems, item => `${item.offerId}-${item.purchasableOption.fkDepartureId}`)
    const itemGroups = itemsByBookingKey.values().map(items => ({
      departureId: items[0].purchasableOption.fkDepartureId,
      offerId: items[0].offerId,
      items,
    })).toArray()

    const views = itemGroups.map((itemGroup): App.WithDataStatus<App.Checkout.TourV2AccommodationOfferView | undefined> => {
      const { offerId, items } = itemGroup
      const offer = offers[offerId] as App.Tours.TourV2Offer
      if (!isTourV2Offer(offer)) {
        if (postPurchase && TOUR_BALANCE_PAYMENT_CHECKOUT_MODES.has(postPurchase)) {
          const orderTourItem = getOrderTourItemForGroup(existingOrder, offerId, itemGroup.departureId)
          if (!orderTourItem) {
            return {
              hasRequiredData: false,
              data: undefined,
            }
          }

          const snapshot = orderTourItem.reservationId ?
            tourV2SnapshotDetails[orderTourItem.reservationId] :
            undefined

          return {
            hasRequiredData: true,
            data: getSnapshotTourV2OfferView(items, orderTourItem, snapshot, serviceFee),
          }
        }

        return {
          hasRequiredData: false,
          data: undefined,
        }
      }

      const firstItem = items[0]
      const { startDate, endDate, duration, image, purchasableOption } = firstItem

      const itemViewsWithStatuses = items.map(
        (item, index) => getTourV2ItemView(getCurrentIntl(), item, index, offer, postPurchase, existingOrder),
      )

      const variationId = offer?.variations[purchasableOption.fkVariationId] ?
        purchasableOption.fkVariationId :
        Object.values(offer?.variations ?? {}).find(variation => variation.fkTourOptionId === purchasableOption.fkVariationId)?.id

      const bundledItemIds = new Set([...flightBundledItemIds, ...accommodationBundledItemIds])

      const offerView: App.Checkout.TourV2AccommodationOfferView = {
        offerId,
        offerPageUrl: offerPageURL(offer),
        ...getLabels(offer, variationId),
        bedGroups: [],
        confidenceLabels: [],
        duration,
        startDate,
        endDate,
        image,
        location: getLocationString(getCurrentIntl(), offer, variationId),
        occupancy: items.map(item => item.occupancy).filter(excludeNullOrUndefined),
        offerType: 'tour_v2',
        reservationType: 'instant_booking',
        urgencyLabels: [],
        saleUnit: 'room',
        durationLabel: intl.formatMessage(
          {
            id: 'DzOQT2DMDMKAv2kdwkwI',
            defaultMessage: '{count, plural, one {# day} other {# days}}',
            description: 'Tour duration label with pluralization on the tour offer detail page',
          },
          { count: duration },
        ),
        offerLoaded: true,
        itemViews: itemViewsWithStatuses.map(i => i.data),
        propertyTimezone: 'GMT',
        operatorName: offer ? intl.formatMessage(
          {
            id: 'iEiRM4lSHHm4Jdq_oqml',
            defaultMessage: 'Operated by: {operatorName}',
            description: 'Label displaying the tour operator name on the tour offer detail overview panel',
          },
          { operatorName: offer.operatedBy },
        ) : '',
        offer,
        designation: intl.formatMessage(productLabels.tour),
        depositType: offer?.depositType,
        depositAmount: offer?.depositAmount ?? undefined,
        serviceFee,
        isBundled: items.some(item => bundledItemIds.has(item.itemId)),
      }

      return {
        data: offerView,
        hasRequiredData: itemViewsWithStatuses.every(view => view.hasRequiredData),
      }
    })

    return {
      data: nonNullable(views.map(view => view.data)),
      hasRequiredData: views.every(view => view.hasRequiredData),
    }
  })

export default getTourV2ItemViews
